import type { Recipe } from './types';

/**
 * Servings-input bounds. Widen or tighten here — never at a call site.
 * `min` is the floor for any recipe; `wholeOnly` recipes additionally snap to
 * whole multiples of their base yield, so their real floor is one full batch.
 */
export const SCALING = {
  minServings: 1,
  maxServings: 99,
} as const;

export const PARTIAL_BATCH_WARNING = 'This recipe may not scale neatly with partial recipes';

function baseServings({ recipe }: { recipe: Recipe }): number {
  const base = recipe.servings ?? 1;
  return base > 0 ? base : 1;
}

/** Lowest servings count this recipe may be scaled to. */
export function minServings({ recipe }: { recipe: Recipe }): number {
  const base = baseServings({ recipe });
  return recipe.wholeOnly ? base : SCALING.minServings;
}

/** Coerce arbitrary input to a servings count the recipe can actually be cooked at. */
export function clampServings({ recipe, servings }: { recipe: Recipe; servings: number }): number {
  const floor = minServings({ recipe });
  if (!Number.isFinite(servings)) return floor;
  return Math.min(Math.max(Math.round(servings), floor), SCALING.maxServings);
}

/** Scaling factor applied to every quantity in the recipe. */
export function multiplierFor({ recipe, servings }: { recipe: Recipe; servings: number }): number {
  return servings / baseServings({ recipe });
}

/** Warning to surface for a live (uncommitted) servings value, or null when it scales cleanly. */
export function scalingWarning({
  recipe,
  servings,
}: {
  recipe: Recipe;
  servings: number;
}): string | null {
  if (!recipe.wholeOnly) return null;
  const multiplier = multiplierFor({ recipe, servings });
  return multiplier > 0 && multiplier % 1 !== 0 ? PARTIAL_BATCH_WARNING : null;
}
